import React, { useEffect, useState } from "react"
import types from "prop-types"
import * as API from "services/api"
import { useReportableFieldLocationAnswerFilter, useSubmissionRelativeDateFilter } from "hooks"
import {
  dayjsOrgTz,
  DAYS, MONTHS, WEEKS,
} from "utils/dateHelpers"
import { reportExportFormSubmissionReportShape } from "utils/propTypeShapes"
import { errorToast, successToast } from "components/shared/toast"
import { ACTIVITY_HUB_FORM_SUBMISSION_REPORTS_PATH, redirectTo } from "utils/routeHelpers"
import { DATE_TYPE } from "views/Forms/FormElementTypes"
import {
  CREATE_SCHEDULED_REPORT_EXPORT_ERROR_MESSAGE,
  MAX_DAYS,
  MAX_WEEKS,
  MAX_MONTHS,
  SUBMISSION_CREATED_DATE_FILTER_HEADER,
  SUBMISSION_UPDATED_DATE_FILTER_HEADER,
  DATE_QUESTION_FILTER_HEADER,
} from "utils/scheduledReportExportHelpers"
import FormSubmissionScheduledReportExportContext from "../FormSubmissionScheduledReportExportContext"

const LOAD_QUESTIONS_ERROR_MESSAGE = "Something went wrong. Unable to load form questions."

const LOCATION_ANSWER_FILTER_TEMPLATE = {
  questionUuid: null,
  departmentIds: [],
}

// Values correspond to backend's ScheduledReportExport :form_submission_timestamp_filter enum options
const ON_CREATED_AT_FILTER = "on_created_at"
const ON_UPDATED_AT_FILTER = "on_updated_at"

export const DATE_FILTER_TYPE_OPTIONS = [SUBMISSION_CREATED_DATE_FILTER_HEADER, SUBMISSION_UPDATED_DATE_FILTER_HEADER, DATE_QUESTION_FILTER_HEADER]

const FormSubmissionScheduledReportExportContextProvider = ({
  children,
  formSubmissionReport: propFormSubmissionReport,
  formSubmissionScheduledReportExport: propFormSubmissionScheduledReportExport = null,
}) => {
  const {
    submissionPreviousRangeLength, submissionPreviousRangeUnit, setSubmissionPreviousRangeLength, setSubmissionPreviousRangeUnit,
  } = useSubmissionRelativeDateFilter()
  const {
    departments,
    facilities,
    facilityDepartments,
  } = useReportableFieldLocationAnswerFilter()

  const [formSubmissionReport] = useState(propFormSubmissionReport)
  const { id: formSubmissionReportId, formSlug } = formSubmissionReport

  const [scheduledReportExport, setScheduledReportExport] = useState({})

  const updatingExistingRecord = !!propFormSubmissionScheduledReportExport

  const [questions, setQuestions] = useState([])
  const [dateAnswerFilters, setDateAnswerFilters] = useState([])
  const [dateFilterType, setDateFilterType] = useState("")

  const [dateAnswerQuestionUuid, setDateAnswerQuestionUuid] = useState()
  const [dateAnswerPreviousRangeLength, setDateAnswerPreviousRangeLength] = useState()
  const [dateAnswerPreviousRangeUnit, setDateAnswerPreviousRangeUnit] = useState()

  const [scheduledReportExportName, setScheduledReportExportName] = useState("")
  const [activeStatus, setActiveStatus] = useState(false)
  const [frequency, setReportFrequency] = useState("")
  const [recurrenceDayOfWeek, setRecurrenceDayOfWeek] = useState("")
  const [recurrenceDayOfMonth, setRecurrenceDayOfMonth] = useState(null)
  const [startDate, setStartDate] = useState(null)

  const [selectedFacilityIds, setSelectedFacilityIds] = useState([])

  const setDateFilterTypeAndClearValues = (selectedDateFilterType) => {
    setDateAnswerPreviousRangeLength()
    setSubmissionPreviousRangeLength()

    setDateFilterType(selectedDateFilterType)
  }

  const maxBasedOnUnit = () => {
    if (submissionPreviousRangeUnit === DAYS) {
      return MAX_DAYS
    } if (submissionPreviousRangeUnit === WEEKS) {
      return MAX_WEEKS
    } if (submissionPreviousRangeUnit === MONTHS) {
      return MAX_MONTHS
    }
    return 10
  }

  const lengthOptions = Array.from({ length: maxBasedOnUnit() }, (_, index) => index + 1)

  const [locationAnswerFilters, setLocationAnswerFilters] = useState([])
  const [selectAnswerFilters, setSelectAnswerFilters] = useState([])

  const toggleSelectAnswerFilterQuestion = (questionUuid) => {
    const exists = selectAnswerFilters.some((f) => f.questionUuid === questionUuid)
    if (exists) {
      setSelectAnswerFilters(selectAnswerFilters.filter((f) => f.questionUuid !== questionUuid))
    } else {
      setSelectAnswerFilters([...selectAnswerFilters, { questionUuid, selectedAnswers: [] }])
    }
  }

  const setSelectAnswerFilterAnswers = (questionUuid, selectedAnswers) => {
    setSelectAnswerFilters(
      selectAnswerFilters.map((f) => (f.questionUuid === questionUuid ? { ...f, selectedAnswers } : f)),
    )
  }

  const setLocationAnswerFilterQuestionUuid = (questionUuid) => {
    let newLocationFilters = []

    if (questionUuid) {
      const currentLocationFilter = locationAnswerFilters[0] ?? { ...LOCATION_ANSWER_FILTER_TEMPLATE }
      newLocationFilters = [{ ...currentLocationFilter, questionUuid }]
    }

    setLocationAnswerFilters(newLocationFilters)
  }

  const updateLocationAnswerFiltersDepartmentIds = (departmentIds) => {
    const currentLocationFilter = locationAnswerFilters[0]

    setLocationAnswerFilters(
      [
        {
          ...currentLocationFilter,
          departmentIds,
        },
      ],
    )
  }

  const addDepartmentIdsToLocationAnswerFilters = (departmentIds) => {
    const currentLocationFilter = locationAnswerFilters[0]
    const updatedDepartmentIds = Array.from(new Set([...currentLocationFilter.departmentIds, ...departmentIds]))
    updateLocationAnswerFiltersDepartmentIds(updatedDepartmentIds)
  }

  const addAllFacilityDepartmentsToLocationAnswerFilters = (facilityId) => {
    addDepartmentIdsToLocationAnswerFilters(
      facilityDepartments(facilityId).map((department) => department.id),
    )
  }

  const removeDepartmentIdsFromLocationAnswerFilters = (departmentIds) => {
    const currentLocationFilter = locationAnswerFilters[0]
    const updatedDepartmentIds = currentLocationFilter.departmentIds.filter((id) => !departmentIds.includes(id))
    updateLocationAnswerFiltersDepartmentIds(updatedDepartmentIds)
  }

  const removeAllFacilityDepartmentsFromLocationAnswerFilters = (facilityId) => {
    removeDepartmentIdsFromLocationAnswerFilters(
      facilityDepartments(facilityId).map((department) => department.id),
    )
  }

  const saveScheduledReportExport = async () => {
    let payload = {
      activeStatus,
      locationAnswerFilters,
      selectAnswerFilters,
      recurrenceDayOfMonth,
      recurrenceDayOfWeek,
      frequency,
      scheduledReportExportName,
      startDate,
      reportId: formSubmissionReportId,
    }

    if (dateFilterType === SUBMISSION_CREATED_DATE_FILTER_HEADER) {
      payload = {
        ...payload,
        dateAnswerFilters: [],
        submissionTimestampFilter: ON_CREATED_AT_FILTER,
        submissionPreviousRangeLength,
        submissionPreviousRangeUnit,
      }
    } else if (dateFilterType === SUBMISSION_UPDATED_DATE_FILTER_HEADER) {
      payload = {
        ...payload,
        dateAnswerFilters: [],
        submissionTimestampFilter: ON_UPDATED_AT_FILTER,
        submissionPreviousRangeLength,
        submissionPreviousRangeUnit,
      }
    } else if (dateFilterType === DATE_QUESTION_FILTER_HEADER) {
      payload = {
        ...payload,
        dateAnswerFilters: [{
          previous_range_length: dateAnswerPreviousRangeLength,
          previous_range_unit: dateAnswerPreviousRangeUnit,
          question_uuid: dateAnswerQuestionUuid,
        }],
        submissionPreviousRangeLength: null,
        submissionPreviousRangeUnit: null,
        submissionTimestampFilter: null,
      }
    }

    let response
    if (updatingExistingRecord) {
      const { id: scheduledReportExportId } = propFormSubmissionScheduledReportExport

      response = await API.updateFormSubmissionScheduledReportExport({ ...payload, scheduledReportExportId })
    } else {
      response = await API.createFormSubmissionScheduledReportExport(payload)
    }

    if (response.ok) {
      setScheduledReportExport(response.data)
      successToast(`Scheduled report export ${scheduledReportExportName} has been saved.`)

      setTimeout(() => { redirectTo(ACTIVITY_HUB_FORM_SUBMISSION_REPORTS_PATH) }, 1000)
    } else {
      errorToast(CREATE_SCHEDULED_REPORT_EXPORT_ERROR_MESSAGE)
    }
  }

  const cancelForm = () => { redirectTo(ACTIVITY_HUB_FORM_SUBMISSION_REPORTS_PATH) }

  useEffect(() => {
    const loadQuestions = async () => {
      const response = await API.formQuestionHistory({ formSlug })

      if (response.ok) {
        const formQuestions = response.data

        setQuestions(formQuestions)

        const dateQuestions = formQuestions.filter((question) => question.type === DATE_TYPE)

        setDateAnswerFilters(dateQuestions)

        if (!updatingExistingRecord) {
          setDateFilterType(dateQuestions.length < 1 ? SUBMISSION_CREATED_DATE_FILTER_HEADER : "")
        }
      } else {
        errorToast(LOAD_QUESTIONS_ERROR_MESSAGE)
      }
    }
    loadQuestions()
  }, [formSlug, updatingExistingRecord])

  // if editing existing scheduled report export:
  useEffect(() => {
    if (updatingExistingRecord) {
      const {
        dateAnswerPreviousRangeLength: propDateAnswerPreviousRangeLength,
        dateAnswerPreviousRangeUnit: propDateAnswerPreviousRangeUnit,
        dateQuestionUuid: propDateQuestionUuid,
        departmentIds,
        facilityIds,
        locationQuestionUuid,
        formSubmissionPreviousRangeLength: propFormSubmissionPreviousRangeLength,
        formSubmissionPreviousRangeUnit: propFormSubmissionPreviousRangeUnit,
        formSubmissionTimestampFilter: propFormSubmissioTimestampFilter,
        name: propName,
        scheduledReportExportSchedule: propScheduledReportExportSchedule,
        selectAnswerFilters: propSelectAnswerFilters,
      } = propFormSubmissionScheduledReportExport

      const {
        active: propActive,
        frequency: propFrequency,
        recurrenceDayOfMonth: propRecurrenceDayOfMonth,
        recurrenceDayOfWeek: propRecurrenceDayOfWeek,
        startDate: propStartDate,
      } = propScheduledReportExportSchedule

      setSubmissionPreviousRangeLength(propFormSubmissionPreviousRangeLength)
      setScheduledReportExportName(propName)
      setActiveStatus(propActive)
      setReportFrequency(propFrequency)
      setRecurrenceDayOfMonth(propRecurrenceDayOfMonth)
      setRecurrenceDayOfWeek(propRecurrenceDayOfWeek)
      setStartDate(dayjsOrgTz(propStartDate))

      setDateAnswerQuestionUuid(propDateQuestionUuid)
      setDateAnswerPreviousRangeLength(propDateAnswerPreviousRangeLength)

      // unit will be the same for both, but only one gets stored on record
      if (propFormSubmissionPreviousRangeUnit) {
        setDateAnswerPreviousRangeUnit(propFormSubmissionPreviousRangeUnit)
        setSubmissionPreviousRangeUnit(propFormSubmissionPreviousRangeUnit)
      } else {
        setDateAnswerPreviousRangeUnit(propDateAnswerPreviousRangeUnit)
        setSubmissionPreviousRangeUnit(propDateAnswerPreviousRangeUnit)
      }

      // There are three top level date filters: form submission created, form submission updated, or by the answer to a date question
      if (propFormSubmissioTimestampFilter) {
        const filterType = propFormSubmissioTimestampFilter === ON_CREATED_AT_FILTER
          ? SUBMISSION_CREATED_DATE_FILTER_HEADER
          : SUBMISSION_UPDATED_DATE_FILTER_HEADER
        setDateFilterType(filterType)
      } else {
        setDateFilterType(DATE_QUESTION_FILTER_HEADER)
      }

      if (locationQuestionUuid) {
        setLocationAnswerFilters([
          {
            questionUuid: locationQuestionUuid,
            departmentIds,
          },
        ])
        setSelectedFacilityIds(facilityIds)
      }

      if (propSelectAnswerFilters?.length > 0) {
        setSelectAnswerFilters(propSelectAnswerFilters)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Object exposed to context consumers
  const contextConsumerValue = {
    activeStatus,
    addAllFacilityDepartmentsToLocationAnswerFilters,
    addDepartmentIdsToLocationAnswerFilters,
    scheduledReportExport,
    cancelForm,
    dateFilterType,
    dateAnswerQuestionUuid,
    dateAnswerPreviousRangeLength,
    dateAnswerFilters,
    departments,
    frequency,
    facilities,
    facilityDepartments,
    lengthOptions,
    locationAnswerFilters,
    recurrenceDayOfMonth,
    recurrenceDayOfWeek,
    removeAllFacilityDepartmentsFromLocationAnswerFilters,
    removeDepartmentIdsFromLocationAnswerFilters,
    dateAnswerPreviousRangeUnit,
    questions,
    saveScheduledReportExport,
    selectedFacilityIds,
    setActiveStatus,
    setSelectedFacilityIds,
    setScheduledReportExport,
    setDateFilterTypeAndClearValues,
    setReportFrequency,
    setRecurrenceDayOfMonth,
    setRecurrenceDayOfWeek,
    setDateAnswerPreviousRangeLength,
    setDateAnswerPreviousRangeUnit,
    setDateAnswerQuestionUuid,
    selectAnswerFilters,
    setLocationAnswerFilterQuestionUuid,
    setScheduledReportExportName,
    setSelectAnswerFilterAnswers,
    toggleSelectAnswerFilterQuestion,
    setStartDate,
    setSubmissionPreviousRangeLength,
    setSubmissionPreviousRangeUnit,
    scheduledReportExportName,
    formSubmissionReport,
    startDate,
    submissionPreviousRangeLength,
    submissionPreviousRangeUnit,
  }

  return (
    <FormSubmissionScheduledReportExportContext.Provider value={contextConsumerValue}>
      {children}
    </FormSubmissionScheduledReportExportContext.Provider>
  )
}

FormSubmissionScheduledReportExportContextProvider.propTypes = {
  children: types.node.isRequired,
  formSubmissionReport: reportExportFormSubmissionReportShape.isRequired,
  formSubmissionScheduledReportExport: types.object,
}

export default FormSubmissionScheduledReportExportContextProvider
